// Shared by BuySellChargesTable, BottomSummaryBar and index.js so the column
// totals shown while editing and the grand total used to build the submit
// payload never drift apart. Rounded to a whole rupee (no decimals) on both
// the Buy Charges and Sell Charges sides — a converted INR total (e.g. from
// a EUR/USD charge) is inherently an approximation, so showing fractional
// paise implied more precision than the conversion actually has.
export const fmt = (n) => Math.round(Number(n) || 0).toLocaleString("en-IN");

// A charge row's own resolved rate for one container column, in whatever
// currency that row was entered in — "SubjectTo"/"Fixed" rows store the rate
// itself in `amounts[ci]`. Used where a line item's own rate/currency needs
// to be reported as-is (e.g. the submit payload's buyRate/sellRate).
export const rawChargeRate = (row, ci) => Number(row?.amounts?.[ci]) || 0;

// Converts an amount from its own currency to INR using the admin-configured
// exchange rates (ratesByCurrency: { USD: <INR per 1 USD>, ... }) — falls
// back to the raw amount if it's already INR or no rate is configured for
// that currency.
export const toInr = (amount, currency, ratesByCurrency) => {
  const n = Number(amount) || 0;
  if (!currency || currency === "INR") return n;
  const rate = Number(ratesByCurrency?.[currency]) || 0;
  return rate > 0 ? n * rate : n;
};

// A charge row's rate for one container column, normalized to INR. Buy/Sell
// Charges rows can each be entered in a different currency (see
// BuySellChargesTable's per-row Currency column), so totals must convert
// every row before summing — otherwise a USD row and an INR row just get
// added together as if they were the same unit.
export const chargeRateInr = (row, ci, ratesByCurrency) =>
  toInr(rawChargeRate(row, ci), row?.currency, ratesByCurrency);

export const grandTotalFor = (charges, containers, ratesByCurrency) =>
  (containers || []).reduce((sum, c, ci) => {
    const colTotal = (charges || []).reduce((s, r) => s + chargeRateInr(r, ci, ratesByCurrency), 0);
    return sum + colTotal * (Number(c.qty) || 0);
  }, 0);

// Whether a charge row's currency can actually be converted to INR — false
// only for a non-INR currency with no admin-configured exchange rate, i.e.
// exactly the case where toInr() silently falls back to the raw, unconverted
// number (e.g. "200 EUR" showing as "200.00 INR" in the grand total). Used
// to warn instead of letting a wrong-looking total pass as if it were real.
export const hasConvertibleRate = (currency, ratesByCurrency) => {
  if (!currency || currency === "INR") return true;
  return Number(ratesByCurrency?.[currency]) > 0;
};
